import { useAuth } from '@/lib/AuthContext'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, ArrowLeft, Ban, Loader2, MoreHorizontal, Eraser, ChevronDown, Send, Trash2, UserMinus, UserRound, X } from 'lucide-react'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { AutoTextarea } from '@/components/ui/auto-textarea'
import { TEAM_RING_CLASSES, teamRoleOf } from './ChatTeam'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { useChat, isDraftId, type PendingMessage } from './ChatProvider'
import { useProfileContext } from '@/components/features/profile/ProfileProvider'
import { ConnectionActions, RemoveConnectionDialog } from './ChatConnections'
import { PresenceDot } from './ChatStatus'
import { lastSeenLabel } from './chatStatus'
import { chatLimitOf, whenFree, type ChatLimit } from './chatLimits'
import { displayPersonName } from '@/lib/personName'

export const MAX_MESSAGE_LENGTH = 4000

/** PADROES-UI §13: a scrolling area says it scrolls. */
export const SCROLL_CLASSES =
  '[scrollbar-color:theme(colors.gray.300)_transparent] [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300'

const timeOf = (iso: string, locale: string) => {
  const date = new Date(iso)
  const today = new Date()
  const sameDay = date.toDateString() === today.toDateString()
  return new Intl.DateTimeFormat(locale, sameDay ? { hour: '2-digit', minute: '2-digit' } : { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }).format(date)
}

const HEADER_BUTTON = 'h-9 w-9 shrink-0 rounded-full text-gray-500 hover:text-gray-900 lg:h-7 lg:w-7'

interface Props {
  conversationId: string
  /** `back` in the side panel and on mobile (returns to the list); `close` on a docked window. */
  onBack?: () => void
  onClose?: () => void
  /** Desktop dock: collapses the window to its header bar. */
  onMinimize?: () => void
  className?: string
}

export const ChatConversationView = ({ conversationId, onBack, onClose, onMinimize, className }: Props) => {
  const { t, i18n } = useTranslation('chat')
  const chat = useChat()
  const conversation = chat.conversations.find((c) => c.id === conversationId)
  /* A window with someone who has no conversation yet: nothing exists until the first message. */
  const isDraft = isDraftId(conversationId)
  const draftEntry = isDraft ? chat.drafts[conversationId] : undefined
  const thread = isDraft ? { items: [], hasMore: false, cursor: null, state: 'ready' as const, loadingOlder: false } : chat.messages[conversationId]
  const other = conversation?.other || draftEntry?.profile || null
  const connectionMode = chat.me?.startMode === 'CONNECTION'
  const connection = conversation?.connection || draftEntry?.connection || 'none'
  /* "Precisa adicionar" without an accepted connection: read-only, with the connection banner on top. */
  /* The server decides for an existing conversation (the team is reachable without a connection, and
     the team can reply); for a draft, the team on either side never needs one. */
  const { hasRole } = useAuth()
  const meIsTeam = hasRole(['owner', 'admin', 'moderator', 'finance'])
  const needsConnection =
    Boolean(other) &&
    !conversation?.blockedByMe &&
    (conversation
      ? Boolean(conversation.needsConnection)
      : connectionMode && connection !== 'connected' && !other?.isTeam && !meIsTeam && !draftEntry?.profile?.canConverse)
  const { openProfile } = useProfileContext()
  const name = displayPersonName(t, other?.name)
  const teamRole = teamRoleOf(other)
  const [draft, setDraft] = React.useState('')
  const [deleting, setDeleting] = React.useState<PendingMessage | null>(null)
  const [blockOpen, setBlockOpen] = React.useState(false)
  const [removeOpen, setRemoveOpen] = React.useState(false)
  const [clearOpen, setClearOpen] = React.useState(false)
  const [busy, setBusy] = React.useState(false)
  const [actionError, setActionError] = React.useState<string | null>(null)
  const scrollRef = React.useRef<HTMLDivElement | null>(null)
  const stickToBottom = React.useRef(true)
  const prependAnchor = React.useRef<number | null>(null)

  const { loadMessages, markRead, registerVisible } = chat

  React.useEffect(() => registerVisible(conversationId), [conversationId, registerVisible])
  const { reportAtBottom } = chat
  React.useEffect(() => {
    reportAtBottom(conversationId, true)
    return () => reportAtBottom(conversationId, true)
  }, [conversationId, reportAtBottom])

  React.useEffect(() => {
    if (!isDraft) loadMessages(conversationId, { silent: true })
  }, [conversationId, isDraft, chat.syncTick, loadMessages])

  /* Without the socket the open conversation is fetched every 30 s (the provider's tick). */
  const unread = conversation?.unreadCount || 0
  React.useEffect(() => {
    if (unread > 0 && document.visibilityState === 'visible') markRead(conversationId)
  }, [unread, conversationId, markRead])

  React.useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && unread > 0) markRead(conversationId)
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [unread, conversationId, markRead])

  const items = React.useMemo(() => thread?.items || [], [thread?.items])
  const typingAt = chat.typing[conversationId]
  const lastId = items[items.length - 1]?.id

  /* Stays at the bottom as messages arrive, unless the person scrolled up to read. */
  React.useLayoutEffect(() => {
    const el = scrollRef.current
    if (!el) return
    if (prependAnchor.current !== null) {
      el.scrollTop = el.scrollHeight - prependAnchor.current
      prependAnchor.current = null
      return
    }
    if (stickToBottom.current) el.scrollTop = el.scrollHeight
  }, [lastId, items.length, typingAt])

  const onScroll = () => {
    const el = scrollRef.current
    if (!el) return
    stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40
    chat.reportAtBottom(conversationId, stickToBottom.current)
    if (el.scrollTop < 60 && thread?.hasMore && !thread.loadingOlder) {
      prependAnchor.current = el.scrollHeight - el.scrollTop
      loadMessages(conversationId, { older: true })
    }
  }

  const canSend = !needsConnection && (conversation ? conversation.canSend !== false : true)

  /* Opened (or brought back) by a person: the cursor goes to the composer, once per request, and only
     when it can send. A request older than a few seconds is stale (a remount must not steal focus). */
  const composerRef = React.useRef<HTMLTextAreaElement | null>(null)
  const handledFocus = React.useRef(0)
  const focusRequest = chat.focusRequest
  React.useEffect(() => {
    if (!focusRequest || focusRequest.id !== conversationId || focusRequest.seq === handledFocus.current) return
    if (Date.now() - focusRequest.at > 4000) return
    if (!canSend) return
    const el = composerRef.current
    if (!el) return
    const seq = focusRequest.seq
    const text = focusRequest.text
    /* preventScroll: the sheet follows the visual viewport itself; iOS must not scroll the page under it.
       Marked as handled only once it ran (StrictMode runs the effect twice and cancels the first frame).
       A suggested text fills an empty composer only: it never overwrites what the person typed, and is not sent. */
    const id = window.requestAnimationFrame(() => {
      handledFocus.current = seq
      if (text) setDraft((current) => (current.trim() ? current : text))
      el.focus({ preventScroll: true })
      /* The caret goes after the suggested text, once React has rendered it. */
      if (text) window.setTimeout(() => el.setSelectionRange(el.value.length, el.value.length), 0)
    })
    return () => window.cancelAnimationFrame(id)
  }, [focusRequest, conversationId, canSend])

  /* The list shrinks when the on-screen keyboard opens (the sheet tracks visualViewport): keep the last
     message in sight if the person was at the bottom. */
  React.useEffect(() => {
    const el = scrollRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => {
      if (stickToBottom.current) el.scrollTop = el.scrollHeight
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  const tooLong = draft.length > MAX_MESSAGE_LENGTH

  /* A chat limit (429): the API's own message inline, the text kept, sending off until retryAt. */
  const [limit, setLimit] = React.useState<ChatLimit | null>(null)
  React.useEffect(() => whenFree(limit?.until ?? null, () => setLimit(null)), [limit])
  const limited = Boolean(limit?.until && limit.until > Date.now())

  const onSendError = (body: string, fallback: string) => (err: unknown) => {
    const hit = chatLimitOf(err)
    if (hit) {
      setDraft(body)
      setLimit(hit)
      return
    }
    if (isDraft) {
      setDraft(body)
      setActionError((err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback)
    }
  }

  const submit = () => {
    const body = draft.trim()
    if (!body || tooLong || !canSend || limited) return
    setDraft('')
    setLimit(null)
    stickToBottom.current = true
    if (isDraft) {
      setActionError(null)
      chat.sendFirstMessage(conversationId, body).catch(onSendError(body, t('errors.cannotMessage')))
      return
    }
    chat.sendMessage(conversationId, body).catch(onSendError(body, t('errors.generic')))
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault()
      submit()
    }
  }

  const confirmDelete = async () => {
    if (!deleting) return
    setBusy(true)
    setActionError(null)
    try {
      await chat.deleteMessage(conversationId, deleting.id)
      setDeleting(null)
    } catch {
      setActionError(t('errors.generic'))
    } finally {
      setBusy(false)
    }
  }

  const confirmClear = async () => {
    setBusy(true)
    setActionError(null)
    try {
      await chat.clearHistory(conversationId)
      setClearOpen(false)
    } catch {
      setActionError(t('errors.generic'))
      setClearOpen(false)
    } finally {
      setBusy(false)
    }
  }

  const toggleBlock = async (blocked: boolean) => {
    if (!other) return
    setBusy(true)
    setActionError(null)
    try {
      await chat.setBlocked(other.id, blocked)
      setBlockOpen(false)
    } catch {
      setActionError(t('errors.generic'))
    } finally {
      setBusy(false)
    }
  }

  /* "Visto": only when both turned it on and nothing blocks (the API sends null otherwise). */
  const seenAt = conversation?.readReceipts && conversation.otherLastReadAt ? new Date(conversation.otherLastReadAt).getTime() : null
  const lastMineSeenId = React.useMemo(() => {
    if (!seenAt) return null
    for (let i = items.length - 1; i >= 0; i -= 1) {
      const m = items[i]
      if (m.mine && !m.sendState && !m.deleted) return new Date(m.createdAt).getTime() <= seenAt ? m.id : null
    }
    return null
  }, [items, seenAt])

  const isTyping = Boolean(chat.typing[conversationId])
  /* F-184, WhatsApp style (David): "online", "ocupado", "ausente", "visto hoje às 14:32" (the full sentence in the
     tooltip) or "offline". Offline, Indisponível and a block look the same on purpose (the API sends the same null). */
  const lastSeenAt = chat.me?.chatShowLastSeen === false ? null : other?.lastSeenAt
  const presenceText = !other
    ? '\u00a0'
    : other.online
      ? <span className={cn(other.status === 'BUSY' ? 'text-red-700' : other.status === 'AWAY' ? 'text-amber-700' : 'text-green-700')} data-chat-other-presence={other.status || 'AVAILABLE'}>
          {other.status === 'BUSY' ? t('people.busy') : other.status === 'AWAY' ? t('people.away') : t('people.online')}
        </span>
      : <span data-chat-other-offline title={lastSeenLabel(t, i18n.language, lastSeenAt) || undefined}>{lastSeenLabel(t, i18n.language, lastSeenAt, 'short') || t('people.offline')}</span>

  return (
    <div className={cn('flex h-full min-h-0 flex-col bg-white', className)}>
      <div className="flex items-center gap-2 border-b border-gray-200 px-3 py-2">
        {onBack && (
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={onBack} aria-label={t('back')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
        )}
        <div className="relative shrink-0">
          <Avatar src={other?.avatar} alt={name} fallback={other?.name} size="sm" className={cn(teamRole && TEAM_RING_CLASSES)} />
          {other && <PresenceDot always online={other.online} status={other.status} className="-bottom-0.5 -right-0.5 h-3 w-3" />}
        </div>
        <div className="min-w-0 flex-1">
          {/* The role chip stays in the lists; here only the avatar ring marks the team (no room). */}
          <p className="truncate text-sm font-semibold text-gray-900">{name}</p>
          <p className="truncate text-xs text-gray-500">
            {isTyping ? <span className="text-primary">{t('conversation.typing')}</span> : presenceText}
          </p>
        </div>
        {/* ⋯, minimize (chevron), close: small and close together; 28 px on desktop, 36 px to tap on mobile. */}
        <div className="flex shrink-0 items-center gap-0.5">
        {other && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className={HEADER_BUTTON} aria-label={t('conversation.options')} title={t('conversation.options')}>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="z-[60]">
              <DropdownMenuItem onClick={() => openProfile(other.id)} data-chat-view-profile>
                <UserRound className="mr-2 h-4 w-4" />{t('conversation.viewProfile')}
              </DropdownMenuItem>
              {!isDraft && (
                <DropdownMenuItem onClick={() => setClearOpen(true)} data-chat-clear-history>
                  <Eraser className="mr-2 h-4 w-4" />{t('conversation.clearHistory')}
                </DropdownMenuItem>
              )}
              {connectionMode && connection === 'connected' && (
                <DropdownMenuItem onClick={() => setRemoveOpen(true)} data-chat-remove-connection>
                  <UserMinus className="mr-2 h-4 w-4" />{t('connections.remove')}
                </DropdownMenuItem>
              )}
              {conversation?.blockedByMe ? (
                <DropdownMenuItem onClick={() => toggleBlock(false)}>
                  <Ban className="mr-2 h-4 w-4" />{t('conversation.unblock')}
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem className="text-red-600" onClick={() => setBlockOpen(true)}>
                  <Ban className="mr-2 h-4 w-4" />{t('conversation.block')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {onMinimize && (
          <Button variant="ghost" size="icon" className={HEADER_BUTTON} onClick={onMinimize} aria-label={t('minimize')} title={t('minimize')} data-chat-minimize>
            <ChevronDown className="h-4 w-4" />
          </Button>
        )}
        {onClose && (
          <Button variant="ghost" size="icon" className={HEADER_BUTTON} onClick={onClose} aria-label={t('close')} title={t('close')}>
            <X className="h-4 w-4" />
          </Button>
        )}
        </div>
      </div>

      {needsConnection && other && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-primary-100 bg-primary-50 px-3 py-2 text-xs text-gray-700" data-chat-needs-connection>
          <span className="min-w-0 flex-1">{t('connections.readOnly', { name })}</span>
          <ConnectionActions
            profileId={other.id}
            state={connection}
            onChange={(next) => (isDraft ? chat.openWith(other.id, undefined, next, { focus: false }).catch(() => {}) : chat.showConversation(conversationId, { focus: false }).catch(() => {}))}
          />
        </div>
      )}
      {!chat.connected && (
        <p className="border-b border-gray-100 bg-gray-50 px-3 py-1.5 text-xs text-gray-500">{t('conversation.offline')}</p>
      )}

      <div ref={scrollRef} onScroll={onScroll} className={cn('min-h-0 flex-1 space-y-1 overflow-y-auto px-3 py-3', SCROLL_CLASSES)}>
        {thread?.loadingOlder && (
          <p className="flex items-center justify-center gap-2 py-2 text-xs text-gray-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />{t('conversation.loadingOlder')}
          </p>
        )}
        {(!thread || thread.state === 'loading') && items.length === 0 && (
          <div className="space-y-3" aria-hidden>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className={cn('flex', i % 2 ? 'justify-end' : 'justify-start')}>
                <div className={cn('h-8 animate-pulse rounded-2xl bg-gray-100', i % 2 ? 'w-1/2' : 'w-2/3')} />
              </div>
            ))}
          </div>
        )}
        {thread?.state === 'failed' && items.length === 0 && (
          <div className="flex flex-col items-center gap-2 py-8 text-center">
            <AlertTriangle className="h-6 w-6 text-amber-500" />
            <p className="text-sm text-gray-600">{t('conversation.loadError')}</p>
            <Button size="sm" variant="outline" onClick={() => loadMessages(conversationId)}>{t('conversation.retry')}</Button>
          </div>
        )}
        {thread?.state === 'ready' && items.length === 0 && (
          <p className="py-8 text-center text-sm text-gray-500">{t('conversation.empty', { name })}</p>
        )}
        {items.map((m) => (
          <div key={m.id} className={cn('group flex flex-col', m.mine ? 'items-end' : 'items-start')}>
            <div className={cn('flex max-w-[85%] items-center gap-1', m.mine && 'flex-row-reverse')}>
              <div
                className={cn(
                  'whitespace-pre-wrap break-words rounded-2xl px-3 py-2 text-sm [overflow-wrap:anywhere]',
                  m.deleted
                    ? 'border border-gray-200 bg-white italic text-gray-400'
                    : m.mine ? 'bg-primary-600 text-primary-foreground' : 'bg-gray-100 text-gray-900',
                  m.sendState === 'sending' && 'opacity-60'
                )}
              >
                {m.deleted ? t('conversation.deleted') : m.body}
              </div>
              {m.mine && !m.deleted && !m.sendState && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      aria-label={t('conversation.messageOptions')}
                      className="rounded p-1 text-gray-400 transition-opacity hover:bg-gray-100 hover:text-gray-600 focus:opacity-100 md:opacity-0 md:group-hover:opacity-100"
                    >
                      <MoreHorizontal className="h-3.5 w-3.5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="z-[60]">
                    <DropdownMenuItem className="text-red-600" onClick={() => setDeleting(m)}>
                      <Trash2 className="mr-2 h-4 w-4" />{t('conversation.deleteMessage')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
            <span className="px-1 pt-0.5 text-[11px] text-gray-400">
              {m.sendState === 'sending' && t('conversation.sending')}
              {m.sendState === 'failed' && (
                <span className="text-red-600">
                  {t('conversation.failed')}{' '}
                  <button type="button" className="font-medium underline" onClick={() => chat.sendMessage(conversationId, m.body || '', m.id).catch(() => {})}>
                    {t('conversation.retry')}
                  </button>
                </span>
              )}
              {!m.sendState && timeOf(m.createdAt, i18n.language)}
              {m.id === lastMineSeenId && <> · {t('conversation.seen')}</>}
            </span>
          </div>
        ))}
      </div>

      <div className="border-t border-gray-200 p-2">
        {canSend ? (
          <>
            <div className="flex items-end gap-2">
              <AutoTextarea
                ref={composerRef}
                data-chat-composer
                value={draft}
                onChange={(e) => {
                  setDraft(e.target.value)
                  if (e.target.value.trim()) chat.sendTyping(conversationId)
                }}
                onKeyDown={onKeyDown}
                placeholder={t('conversation.placeholder')}
                aria-label={t('conversation.placeholder')}
                maxHeight={120}
                rows={1}
                className="min-w-0 flex-1 resize-none rounded-2xl border border-gray-300 px-3 py-2 text-base focus:border-primary focus:outline-none sm:text-sm"
              />
              <Button size="icon" variant="secondary" className="h-9 w-9 shrink-0" onClick={submit} disabled={!draft.trim() || tooLong || limited} aria-label={t('conversation.send')}>
                <Send className="h-4 w-4" />
              </Button>
            </div>
            {limit?.message && (
              <p className="mt-1 rounded-md bg-amber-50 px-2 py-1 text-xs text-amber-800" role="status" data-chat-limit>{limit.message}</p>
            )}
            {draft.length > MAX_MESSAGE_LENGTH - 200 && (
              <p className={cn('mt-1 text-xs', tooLong ? 'text-red-600' : 'text-gray-500')}>
                {t('conversation.tooLong', { max: MAX_MESSAGE_LENGTH, count: draft.length })}
              </p>
            )}
          </>
        ) : (
          needsConnection ? (
            <p className="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-500">{t('connections.composerLocked')}</p>
          ) : (
          <div className="flex items-center justify-between gap-2 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600">
            <span>{conversation?.blockedByMe ? t('conversation.blockedByMe') : t('conversation.cannotSend')}</span>
            {conversation?.blockedByMe && (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => toggleBlock(false)}>{t('conversation.unblock')}</Button>
            )}
          </div>
          )
        )}
        {actionError && <p className="mt-1 text-xs text-red-600">{actionError}</p>}
      </div>

      <Dialog open={Boolean(deleting)} onOpenChange={(open) => !open && setDeleting(null)}>
        <DialogContent className="w-[calc(100%-1.5rem)] max-w-md p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>{t('confirmDelete.title')}</DialogTitle>
            <DialogDescription>{t('confirmDelete.body')}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="w-full sm:w-auto" onClick={() => setDeleting(null)}>{t('confirmDelete.cancel')}</Button>
            <Button variant="destructive" className="w-full sm:w-auto" disabled={busy} onClick={confirmDelete}>
              {t('confirmDelete.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {other && (
        <RemoveConnectionDialog
          profileId={other.id}
          name={name}
          open={removeOpen}
          onOpenChange={setRemoveOpen}
          onRemoved={() => chat.showConversation(conversationId, { focus: false }).catch(() => {})}
        />
      )}

      <Dialog open={clearOpen} onOpenChange={setClearOpen}>
        <DialogContent className="w-[calc(100%-1.5rem)] max-w-md p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>{t('confirmClear.title')}</DialogTitle>
            <DialogDescription>{t('confirmClear.body', { name })}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="w-full sm:w-auto" onClick={() => setClearOpen(false)}>{t('confirmClear.cancel')}</Button>
            <Button variant="destructive" className="w-full sm:w-auto" disabled={busy} onClick={confirmClear} data-chat-clear-confirm>
              {t('confirmClear.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={blockOpen} onOpenChange={setBlockOpen}>
        <DialogContent className="w-[calc(100%-1.5rem)] max-w-md p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>{t('confirmBlock.title', { name })}</DialogTitle>
            <DialogDescription>{t('confirmBlock.body')}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="w-full sm:w-auto" onClick={() => setBlockOpen(false)}>{t('confirmBlock.cancel')}</Button>
            <Button variant="destructive" className="w-full sm:w-auto" disabled={busy} onClick={() => toggleBlock(true)}>
              {t('confirmBlock.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
